/**
 * Profile Page
 * View and edit user profile, change password, update avatar
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { User, Lock, Upload, Camera, X, Save } from 'lucide-react'
import { useMyProfile, useUpdateProfile } from '@/hooks/useProfile'
import { toast } from '@/lib/toast'
import { PageLoading } from '@/components/loading'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PasswordChangeModal } from './PasswordChangeModal'
import { AvatarUpload } from './AvatarUpload'

const DEFAULT_AVATAR = 'https://res.cloudinary.com/dcjkrpskm/image/upload/v1675989086/no_profile_k2cffs_day1zx.png'

export function ProfilePage() {
  const { data: user, isLoading, error } = useMyProfile()
  const updateProfile = useUpdateProfile()
  const [searchParams, setSearchParams] = useSearchParams()

  const [isEditing, setIsEditing] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showAvatarUpload, setShowAvatarUpload] = useState(false)

  // Auto-open the password modal when arriving from the expiry nag
  // (e.g. /profile?changePassword=1)
  useEffect(() => {
    if (searchParams.get('changePassword') === '1') {
      setShowPasswordModal(true)
      searchParams.delete('changePassword')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    birthday: '',
    contact_number: '',
  })

  // Load user data into form when available
  useEffect(() => {
    if (user?.profile) {
      setFormData({
        firstname: user.profile.firstname || '',
        lastname: user.profile.lastname || '',
        birthday: user.profile.birthday || '',
        contact_number: user.profile.contact_number || '',
      })
    }
  }, [user])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      await updateProfile.mutateAsync(formData)
      toast.success('Profile updated successfully')
      setIsEditing(false)
    } catch (error: any) {
      toast.error('Failed to update profile', error.message)
    }
  }

  const handleCancel = () => {
    // Reset form to original values
    if (user?.profile) {
      setFormData({
        firstname: user.profile.firstname || '',
        lastname: user.profile.lastname || '',
        birthday: user.profile.birthday || '',
        contact_number: user.profile.contact_number || '',
      })
    }
    setIsEditing(false)
  }

  if (isLoading) {
    return <PageLoading message="Loading profile..." />
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-lg text-red-600">Failed to load profile</p>
          <p className="text-sm text-gray-600 mt-2">{error?.message}</p>
        </div>
      </div>
    )
  }

  const avatarUrl = user.profile?.profile_picture_url || DEFAULT_AVATAR
  const fullName = user.profile
    ? `${user.profile.firstname || ''} ${user.profile.lastname || ''}`.trim()
    : user.username
  const userRole = user.roles?.[0]?.role || 'User'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
          <User className="h-8 w-8" />
          My Profile
        </h1>
        <p className="text-gray-600 mt-1">
          Manage your personal information and account settings
        </p>
      </div>

      {/* Profile Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Avatar */}
            <div className="relative group">
              <img
                src={avatarUrl}
                alt={fullName}
                className="w-32 h-32 rounded-full object-cover border-4 border-primary/20"
              />
              <button
                onClick={() => setShowAvatarUpload(true)}
                className="absolute bottom-0 right-0 p-2 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors shadow-lg"
                title="Change profile picture"
              >
                <Camera className="w-4 h-4" />
              </button>
            </div>

            {/* User Info */}
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold text-gray-900">{fullName || user.username}</h2>
              <p className="text-gray-600">@{user.username}</p>
              <div className="mt-2 flex flex-wrap gap-2 justify-center md:justify-start">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                  {userRole}
                </span>
                {user.is_active && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                )}
              </div>
              {user.email && (
                <p className="text-gray-600 mt-2">
                  <span className="font-medium">Email:</span> {user.email}
                </p>
              )}
              <p className="text-gray-500 text-sm mt-1">
                Member since {new Date(user.date_created).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="profile">Profile Information</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        {/* Profile Information Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Personal Information</CardTitle>
                  <CardDescription>
                    Update your personal details and contact information
                  </CardDescription>
                </div>
                {!isEditing && (
                  <Button onClick={() => setIsEditing(true)} className="bg-primary">
                    <User className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* First Name */}
                  <div className="space-y-2">
                    <Label htmlFor="firstname">First Name</Label>
                    <Input
                      id="firstname"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={!isEditing ? 'bg-gray-50' : ''}
                    />
                  </div>

                  {/* Last Name */}
                  <div className="space-y-2">
                    <Label htmlFor="lastname">Last Name</Label>
                    <Input
                      id="lastname"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={!isEditing ? 'bg-gray-50' : ''}
                    />
                  </div>

                  {/* Birthday */}
                  <div className="space-y-2">
                    <Label htmlFor="birthday">Birthday</Label>
                    <Input
                      id="birthday"
                      name="birthday"
                      type="date"
                      value={formData.birthday}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={!isEditing ? 'bg-gray-50' : ''}
                    />
                  </div>

                  {/* Contact Number */}
                  <div className="space-y-2">
                    <Label htmlFor="contact_number">Contact Number</Label>
                    <Input
                      id="contact_number"
                      name="contact_number"
                      value={formData.contact_number}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={!isEditing ? 'bg-gray-50' : ''}
                      placeholder="09XXXXXXXXX"
                      maxLength={11}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                {isEditing && (
                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={updateProfile.isPending}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-primary"
                      disabled={updateProfile.isPending}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>
                Manage your password and security preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-medium">Password</h3>
                    <p className="text-sm text-gray-600">
                      Last changed: {new Date(user.date_updated).toLocaleDateString()}
                    </p>
                  </div>
                  <Button onClick={() => setShowPasswordModal(true)} className="bg-primary">
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                </div>

                <div className="p-4 bg-gray-50 border rounded-lg">
                  <h3 className="font-medium mb-2">Account Information</h3>
                  <div className="space-y-1 text-sm">
                    <p>
                      <span className="font-medium">Username:</span> {user.username}
                    </p>
                    <p>
                      <span className="font-medium">Email:</span> {user.email || 'Not set'}
                    </p>
                    <p>
                      <span className="font-medium">Account Status:</span>{' '}
                      <span className={user.is_active ? 'text-green-600' : 'text-red-600'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <PasswordChangeModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />

      <AvatarUpload
        open={showAvatarUpload}
        onClose={() => setShowAvatarUpload(false)}
        currentAvatar={avatarUrl}
      />
    </div>
  )
}

export default ProfilePage
